<template>
    <div class="rp-icon-wrap" :style="wrapStyle">
        <div class="rp-running" :style="runningStyle">
            <div class="rp-outer">
                <div class="rp-body">
                    <div class="rp-arm rp-behind"></div>
                    <div class="rp-arm rp-front"></div>
                    <div class="rp-leg rp-behind"></div>
                    <div class="rp-leg rp-front"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "RunningPersonIcon",
    props: {
        size: { type: [Number, String], default: 28 },
        color: { type: String, default: "" },
    },
    computed: {
        wrapStyle() {
            const size = Number(this.size) || 28;
            const style = { width: `${size}px`, height: `${size}px` };
            if (this.color) style.color = this.color;
            return style;
        },
        runningStyle() {
            const size = Number(this.size) || 28;
            const scale = (size / 28) * 0.5;
            return { transform: `scale(${scale})` };
        },
    },
};
</script>

<style scoped>
/* ── 래퍼 ──────────────────────────────── */
.rp-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
    flex-shrink: 0;
}

.rp-running {
    --color: currentColor;
    --duration: 0.8s;
    transform-origin: 4px 9px; /* 머리 중심 기준으로 축소 */
}

/* ── 외부 바운스 ────────────────────────── */
.rp-outer {
    animation: rp-outer var(--duration) linear infinite;
}

/* ── 몸통 ──────────────────────────────── */
.rp-body {
    position: relative;
    background: var(--color);
    width: 8px;
    height: 15px;
    border-radius: 4px;
    transform-origin: 4px 11px;
    animation: rp-body var(--duration) linear infinite;
}

/* 머리 */
.rp-body::before {
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    bottom: 16px;
    left: 0;
    background: var(--color);
}

/* ── 팔 공통 ───────────────────────────── */
.rp-arm {
    position: absolute;
    width: 11px;
    height: 4px;
    top: 0;
    left: 2px;
    border-radius: 2px;
    transform-origin: 2px 2px;
    background: var(--color);
}

/* 앞팔 전완 (elbow joint) */
.rp-arm::before {
    content: "";
    position: absolute;
    width: 11px;
    height: 4px;
    top: 0;
    left: 7px;
    border-radius: 2px;
    transform-origin: 2px 2px;
    background: var(--color);
}

/* ── 앞팔 ──────────────────────────────── */
.rp-arm.rp-front {
    animation: rp-arm-front var(--duration) linear infinite;
}
.rp-arm.rp-front::before {
    animation: rp-arm-front-b var(--duration) linear infinite;
}

/* ── 뒷팔 ──────────────────────────────── */
.rp-arm.rp-behind {
    animation: rp-arm-behind var(--duration) linear infinite;
}
.rp-arm.rp-behind::before {
    animation: rp-arm-behind-b var(--duration) linear infinite;
}

/* ── 다리 공통 ─────────────────────────── */
.rp-leg {
    position: absolute;
    width: 12px;
    height: 4px;
    top: 11px;
    left: 2px;
    border-radius: 2px;
    transform-origin: 2px 2px;
}

/* 다리 하퇴 (knee joint) */
.rp-leg::before {
    content: "";
    position: absolute;
    width: 12px;
    height: 4px;
    top: 0;
    left: 8px;
    border-radius: 2px;
    transform-origin: 2px 2px;
    background: var(--color);
}

/* ── 앞다리 ────────────────────────────── */
.rp-leg.rp-front {
    background: var(--color);
    animation: rp-leg-front var(--duration) linear infinite;
}
.rp-leg.rp-front::before {
    animation: rp-leg-front-b var(--duration) linear infinite;
}

/* ── 뒷다리 (대퇴는 투명, ::before·::after가 가시) */
.rp-leg.rp-behind {
    background: transparent;
    animation: rp-leg-behind var(--duration) linear infinite;
}
.rp-leg.rp-behind::before {
    animation: rp-leg-behind-b var(--duration) linear infinite;
}

/* 뒷발 (shoe) */
.rp-leg.rp-behind::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    height: 4px;
    width: 6px;
    clip-path: polygon(2px 0, 6px 0, 6px 4px, 0 4px);
    border-radius: 0 2px 2px 0;
    background: var(--color);
}

/* ══ 키프레임 ═══════════════════════════════ */

/* 외부: 이중 바운스 */
@keyframes rp-outer {
    0%,
    100% {
        transform: translateY(0);
    }
    25%,
    75% {
        transform: translateY(4px);
    }
    50% {
        transform: translateY(0);
    }
}

/* 몸통 흔들림 */
@keyframes rp-body {
    0%,
    100% {
        transform: rotate(32deg);
    }
    25%,
    75% {
        transform: rotate(24deg);
    }
    50% {
        transform: rotate(16deg);
    }
}

/* 앞팔 상완: 24° → 164° → 24° */
@keyframes rp-arm-front {
    0%,
    100% {
        transform: rotate(24deg);
    }
    50% {
        transform: rotate(164deg);
    }
}

/* 앞팔 전완: -48° ↔ -36° (arm-b 타이밍) */
@keyframes rp-arm-front-b {
    0%,
    100% {
        transform: rotate(-48deg);
    }
    30%,
    70% {
        transform: rotate(-36deg);
    }
}

/* 뒷팔 상완: 164° → 24° → 164° (앞팔 반대 위상) */
@keyframes rp-arm-behind {
    0%,
    100% {
        transform: rotate(164deg);
    }
    50% {
        transform: rotate(24deg);
    }
}

/* 뒷팔 전완: -36° ↔ -48° */
@keyframes rp-arm-behind-b {
    0%,
    100% {
        transform: rotate(-36deg);
    }
    30%,
    70% {
        transform: rotate(-48deg);
    }
}

/* 앞다리 대퇴: 10° → 108° → 10° */
@keyframes rp-leg-front {
    0%,
    100% {
        transform: rotate(10deg);
    }
    50% {
        transform: rotate(108deg);
    }
}

/* 앞다리 하퇴: 18° → 76° → 18° */
@keyframes rp-leg-front-b {
    0%,
    100% {
        transform: rotate(18deg);
    }
    50% {
        transform: rotate(76deg);
    }
}

/* 뒷다리 대퇴 (투명): 108° → 10° → 108° */
@keyframes rp-leg-behind {
    0%,
    100% {
        transform: rotate(108deg);
    }
    50% {
        transform: rotate(10deg);
    }
}

/* 뒷다리 하퇴: 76° → 18° → 76° */
@keyframes rp-leg-behind-b {
    0%,
    100% {
        transform: rotate(76deg);
    }
    50% {
        transform: rotate(18deg);
    }
}
</style>
